import { observer } from 'mobx-react';
import React, { useReducer, useEffect, useState } from 'react';
import {
    ActionList,
    AppCard,
    AppFooter,
    AppHeader,
    AppListLoading,
    AppSection,
    AppSectionHeader,
    Form,
    InputPhone,
    InputSwitch,
    S3FileUploader,
    useSnackbar
} from '../../components-v2/shared';
import { DateTime } from 'luxon';
import useStore from '../../store/useStore';
import { initialFormValues } from '../ApplicationSettings/ApplicationSettings.helpers';
import { CompanyDetailsMembers } from './CompanyDetailsMembers';
import {
    companyDetailsFormSchema,
    CompanyDetailsFormValues,
    CompanyDetailsReducer,
    EnablingSMSStatus,
    getCompanyLogoPreview
} from './CompanyDetailsForm.helpers';
import {
    Button,
    ButtonPrimary,
    handlePromise,
    InputDate,
    InputSelect,
    Modal,
    ModalActions,
    ModalHeader,
    useAsyncEffect
} from '@lambdacurry/component-library';
import { CompanyDetailsSMSNumber } from './CompanyDetailsSMSNumber';
import { CompanyDetailsAdminSettings } from './CompanyDetailsAdminSettings';
import { CompanyDetailsNotificationSettings } from './CompanyDetailsNotificationSettings';
import { CompanyDetailsSettings } from './CompanyDetailsSettings';
import { SpamManagementSection } from './SpamManagementSection';
import './company-details.scss';
import { isNil, omitBy } from 'lodash';
import { FormikHelpers, FormikProps, isString } from 'formik';
import { unsavedChangesContainerSelectorDefault } from '../../constants';
import { EmbedTab } from '../ApplicationSettings/AdvancedSettings/Tabs';
import { ApplicationSettingsFormValues } from '../ApplicationSettings/ApplicationSettings.helpers';
import { CLiQCompanySMSConfig, Company, PublicRole } from '../../types';
import { ensureCompanyMasterId } from './ensureCompanyMasterId';
import { ensureFullCompany, isCompanyDetailsFormReady } from './ensureFullCompany';

const getApplicationSettingsInitialValues = (publicRole?: PublicRole): ApplicationSettingsFormValues => ({
    ...initialFormValues,
    ...omitBy(publicRole || {}, isNil)
});

const UniversalTccEmbedPanel: React.FC = observer(() => {
    const { store } = useStore();
    const [loadedCompanyId, setLoadedCompanyId] = useState<number | null>(null);
    const [hasError, setHasError] = useState(false);
    const { activeCompanyId, activeCompanyPublicRole } = store;
    const isLoading = loadedCompanyId !== activeCompanyId && !hasError;

    useEffect(() => {
        if (!activeCompanyId) {
            return;
        }

        let isMounted = true;
        setHasError(false);

        store
            .fetchPublicRoles()
            .then(() => {
                if (isMounted) {
                    setLoadedCompanyId(activeCompanyId);
                }
            })
            .catch(error => {
                console.error('Failed to load Universal TCC application code.', error);
                if (isMounted) {
                    setHasError(true);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [store, activeCompanyId]);

    if (isLoading) {
        return (
            <AppCard className="advanced-settings-embed">
                <AppSectionHeader title="Application Code" />
                <p className="clx-margin-0">Loading application code.</p>
            </AppCard>
        );
    }

    if (hasError || !activeCompanyPublicRole) {
        return (
            <AppCard className="advanced-settings-embed">
                <AppSectionHeader title="Application Code" />
                <p className="clx-margin-0">
                    Application code is unavailable because no public role was found for this company.
                </p>
            </AppCard>
        );
    }

    return (
        <Form confirmUnsavedChanges initialValues={getApplicationSettingsInitialValues(activeCompanyPublicRole)} onSubmit={() => { }} validateOnMount>
            {(appFormikProps: FormikProps<ApplicationSettingsFormValues>) => <EmbedTab formikProps={appFormikProps} />}
        </Form>
    );
});

export const CompanyDetailsForm: React.FC<{
    title: string;
    onSubmit: (
        values: CompanyDetailsFormValues,
        formikHelpers: FormikHelpers<CompanyDetailsFormValues>,
        isActive?: boolean
    ) => void;
}> = observer(({ title, onSubmit }) => {
    const { store } = useStore();
    const { addSnackbar } = useSnackbar();
    const {
        activeCompany,
        isAdminRole,
        isSuperAdminRole,
        isAgencyRole,
        isCompanyLightRole,
        activeUser,
        Api,
        twilioNumbers,
        fetchTwilioNumbers,
        refreshCompany,
        fetchPublicRoles
    } = store;
    const [state, dispatch] = useReducer(CompanyDetailsReducer, {
        initialCompany: activeCompany,
        fileUploading: false,
        enablingSMSStatus: EnablingSMSStatus.LOADING,
        endTrialEarlyModal: false,
        trialDismissed: false,
        isCompanyActive: activeCompany?.active ?? true,
        isCompanyPaused: activeCompany?.is_paused ?? false
    });

    const isCreateCompanyForm = title === 'Add Company';
    const [smsConfigurations, setSmsConfigurations] = useState<CLiQCompanySMSConfig[]>([]);
    const [cliqDashboardUrl, setCliqDashboardUrl] = useState<string>();
    const [hydratedCompanyId, setHydratedCompanyId] = useState<number | null>(null);
    const [hydrationError, setHydrationError] = useState(false);

    const loadFullCompany = async (id: number) => {
        await refreshCompany(id);
        const fullCompany = store.companies.getItem(id);
        if (fullCompany) {
            store.upsertAccessibleCompany(fullCompany);
        }
        return fullCompany;
    };

    const fetchSmsConfigurations = async (masterId = store.activeCompany?.master_id): Promise<CLiQCompanySMSConfig[]> => {
        if (!masterId) {
            return [];
        }

        const [response, error] = await handlePromise(Api.client.get(`/sms/sms-configurations/${masterId}`));
        if (!response || error) {
            console.error('Failed to fetch CLiQ Messages SMS configurations.', error);
            return [];
        }

        return Array.isArray(response.data) ? (response.data as CLiQCompanySMSConfig[]) : [];
    };

    const fetchCliqDashboardUrl = async (masterId = store.activeCompany?.master_id): Promise<string | undefined> => {
        if (!masterId) {
            return undefined;
        }

        const [response, error] = await handlePromise(Api.client.get(`/sms/sms-configurations/${masterId}/dashboard-url`));
        if (!response || error || !response.data.url) {
            console.error('Failed to fetch the CLiQ Dashboard URL.', error);
            return undefined;
        }

        return response.data.url;
    };

    const syncSmsConfigurations = async (): Promise<boolean> => {
        const masterId = await ensureCompanyMasterId(store.activeCompany, loadFullCompany);
        if (!masterId) {
            addSnackbar('This company does not have a CLiQ Messages master ID.', { variant: 'error' });
            return false;
        }

        const [response, error] = await handlePromise(Api.client.post(`/sms/sms-configurations/${masterId}`));
        if (!response || error) {
            console.error('Failed to sync CLiQ Messages SMS configurations.', error);
            addSnackbar(
                'No valid SMS configurations found for this company. Check the CLiQ Messages configuration in the CLiQ Dashboard for more information.',
                { variant: 'warning' }
            );
            return false;
        }

        if (!Array.isArray(response.data) || response.data.length === 0) {
            addSnackbar('No SMS configuration found for this company.', { variant: 'warning' });
            return false;
        }

        await Promise.all([fetchTwilioNumbers(), fetchSmsConfigurations(masterId).then(setSmsConfigurations)]);
        addSnackbar('SMS number configuration updated from CLiQ Messages.', { variant: 'success' });
        return true;
    };

    const fetchData = async () => {
        window.scrollTo(0, 0);
        if (isCreateCompanyForm) {
            return;
        }

        const requestedCompanyId = store.activeCompanyId;
        dispatch({ name: 'setEnablingSMSStatus', payload: EnablingSMSStatus.LOADING });
        setHydrationError(false);
        setSmsConfigurations([]);
        setCliqDashboardUrl(undefined);

        let fullCompany: Company | undefined;
        try {
            fullCompany = await ensureFullCompany(
                store.activeCompany,
                loadFullCompany,
                requestedCompanyId
            );
        } catch (error) {
            console.error('Failed to load full company for settings.', error);
        }
        if (store.activeCompanyId !== requestedCompanyId) {
            return;
        }

        if (!fullCompany) {
            setHydrationError(true);
            return;
        }

        setHydratedCompanyId(requestedCompanyId);

        const masterId = fullCompany.master_id;
        const [, , smsConfigs, dashboardUrl] = await Promise.all([
            fetchTwilioNumbers(),
            fetchPublicRoles(),
            fetchSmsConfigurations(masterId),
            fetchCliqDashboardUrl(masterId)
        ]);
        if (store.activeCompanyId !== requestedCompanyId) {
            return;
        }

        setSmsConfigurations(smsConfigs);
        setCliqDashboardUrl(dashboardUrl);
        dispatch({ name: 'setEnablingSMSStatus', payload: EnablingSMSStatus.WAITING });
    };

    useAsyncEffect(fetchData, undefined, [store.activeCompanyId]);

    useEffect(() => {
        dispatch({
            name: 'toggleIsCompanyPaused',
            payload: activeCompany?.is_paused ?? false
        });
    }, [activeCompany]);

    // Check if Twilio Numbers has loaded
    useEffect(() => {
        if (!twilioNumbers.values) {
            return;
        }

        if (state.enablingSMSStatus === EnablingSMSStatus.LOADING) {
            dispatch({ name: 'setEnablingSMSStatus', payload: EnablingSMSStatus.WAITING });
        }
    }, [twilioNumbers.values, state.enablingSMSStatus]);

    const formatDateInput = (input?: string | null | Date): Date | null => {
        if (!input) {
            return null;
        }
        if (isString(input)) {
            return DateTime.fromISO(input)
                .startOf('day')
                .toJSDate();
        }

        try {
            return DateTime.fromJSDate(input as Date)
                .startOf('day')
                .toJSDate();
        } catch (e) {
            return null;
        }
    };

    // Note we might need to determine whether or not this form is a create or edit form to determine what the initial values should be
    const initialValues: CompanyDetailsFormValues = isCreateCompanyForm
        ? {
            active: true,
            country: 'US',
            name: '',
            logo: '',
            logo_file: [],
            agency_id: undefined,
            address1: '',
            address2: '',
            city: '',
            state: '',
            zipcode: '',
            time_zone: '',
            website_url: '',
            dma: 0,
            business_category_id: 0,
            is_test: false,
            is_universal: false,
            is_paused: false,
            weekly_notification: false,
            weekly_lead_notification_email: '',
            lead_notification_primary_emails: '',
            lead_notification_cc_emails: '',
            reply_to_email: '',
            phone: '',
            crm_config_id: null,
            ownership_group_id: null,
            property_management_company_id: null,
            region_id: null,
            enquire_portal_id: undefined,
            plain_text_lead_notify: false,
            assigned_users: undefined,
            expiration_date: null,
            is_forward_sms_enable: false,
            sms_forward_phone_number: null,
            sms_forward_notification_start_time: null,
            sms_forward_notification_end_time: null,
            logo_background_color: 'white'
        }
        : {
            ...activeCompany,
            country: activeCompany?.country || 'US',
            logo_file: getCompanyLogoPreview(activeCompany),
            expiration_date: formatDateInput(activeCompany?.expiration_date),
            logo_background_color: activeCompany?.logo_background_color ?? 'white'
        };

    const trialEndDate = DateTime.fromISO(activeCompany?.trial_end_date || '');
    const trialHasExpired =
        trialEndDate.startOf('day').toSeconds() <=
        DateTime.local()
            .startOf('day')
            .toSeconds();
    const shouldShowTrialEnding =
        (isAdminRole || isAgencyRole) && trialEndDate.isValid && !trialHasExpired && !state.trialDismissed;

    const endTrialEarly = async () => {
        const [response, error] = await handlePromise(
            Api.client.post(`/companies/${activeCompany.id}/endTrial`, { user: activeUser })
        );

        if (!response || error) {
            console.error('Error ending trial early');
        }

        // Note: Once the trial is ended we want to dismiss the alert and fetch companies to show the trial ended banner
        dispatch({ name: 'dismissTrial' });
        refreshCompany(activeCompany.id);
    };

    const toggleActiveCompany = async (formikProps: FormikProps<CompanyDetailsFormValues>) => {
        try {
            const validate = await formikProps.validateForm();
            const errors = Object.keys(validate)?.length;
            if (!errors) {
                onSubmit(formikProps.values, formikProps, state.isCompanyActive);
                dispatch({ name: 'toggleIsCompanyActive' });
                addSnackbar(`The company has been ${!state.isCompanyActive ? 'deactivated' : 'activated'}.`, {
                    variant: 'success'
                });
            }
            formikProps.submitForm();
        } catch (error) {
            console.error(error.response.data);
            addSnackbar(`Failed to ${!state.isCompanyActive ? 'deactivate' : 'activate'} the company.`, {
                variant: 'error'
            });
        }
    };
    const TimeRangeList = Array.from({ length: 24 }, (_, i) => ({
        time: i.toString().padStart(2, '0') + ':00'
    }));
    const hasProvisionedSmsNumber = twilioNumbers.values.length > 0;
    const hasTelnyxSmsNumber = twilioNumbers.values.some(number => number.sid.startsWith('telnyx-'));
    const isFormReady = isCompanyDetailsFormReady({
        isCreateCompanyForm,
        activeCompanyId: store.activeCompanyId,
        hydratedCompanyId
    });

    if (hydrationError) {
        return (
            <div className="company-details">
                <AppCard>
                    <AppSectionHeader title="Couldn't load company settings" />
                    <p>The full company record did not load. Try again.</p>
                    <ButtonPrimary onClick={() => fetchData()}>Try again</ButtonPrimary>
                </AppCard>
            </div>
        );
    }

    if (!isFormReady) {
        return (
            <div className="company-details">
                <AppListLoading title="Loading company settings" />
            </div>
        );
    }

    return (
        <div className="company-details">
            <Form
                key={hydratedCompanyId ?? 'create-company'}
                confirmUnsavedChanges
                initialValues={initialValues}
                validationSchema={companyDetailsFormSchema}
                onSubmit={onSubmit}
                unsavedChangesConfig={{
                    containerQuerySelectorAll: unsavedChangesContainerSelectorDefault
                }}
            >
                {(formikProps: FormikProps<CompanyDetailsFormValues>) => (
                    <>
                        <AppHeader title={title}>
                            <ActionList position="end">
                                {state.isCompanyPaused && (
                                    <div
                                        className="company-details-paused"
                                        style={{
                                            margin: '10px 20px 0px',
                                            border: '1px solid gray',
                                            backgroundColor: '#F8D7DA',
                                            borderRadius: '12px',
                                            padding: '8px 20px'
                                        }}
                                    >
                                        <div>
                                            <p
                                                style={{
                                                    fontSize: '16px',
                                                    color: '#FF0000',
                                                    fontWeight: 600,
                                                    marginBottom: 0
                                                }}
                                            >
                                                Company is currently paused
                                            </p>
                                            <p>
                                                Editing is still available but no applications will be served while
                                                account is paused.
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {isCreateCompanyForm && (
                                    <InputSwitch
                                        name="active"
                                        labelOn="Active"
                                        labelOff="Inactive"
                                        labelPlacement="start"
                                        formikProps={formikProps}
                                    />
                                )}

                                {!isCreateCompanyForm && (isAdminRole || isAgencyRole) && (
                                    <InputSwitch
                                        name="active"
                                        labelOn="Active"
                                        labelOff="Inactive"
                                        labelPlacement="start"
                                        onClick={() => toggleActiveCompany(formikProps)}
                                        checked={state.isCompanyActive}
                                    />
                                )}
                                <InputDate
                                    unselectable="on"
                                    disabled={!isSuperAdminRole}
                                    label="Expiration Date"
                                    name="expiration_date"
                                    disablePast={true}
                                    formikProps={formikProps}
                                    style={{ top: 12 }}
                                />
                            </ActionList>
                        </AppHeader>

                        {shouldShowTrialEnding && (
                            <>
                                <div className="company-details-trial">
                                    <p>
                                        Company trial is ending in{' '}
                                        {Math.ceil(trialEndDate.diffNow(['days']).toObject().days || 0)} days.
                                    </p>
                                    <ButtonPrimary onClick={() => dispatch({ name: 'toggleEndTrialModal' })}>
                                        End Trial Early
                                    </ButtonPrimary>
                                </div>

                                <Modal
                                    isOpen={state.endTrialEarlyModal}
                                    onAfterClose={() => dispatch({ name: 'toggleEndTrialModal' })}
                                >
                                    <ModalHeader title="Are you sure you want to end the 30 day trial early?" />
                                    <ModalActions>
                                        <ActionList position="end">
                                            <Button onClick={() => dispatch({ name: 'toggleEndTrialModal' })}>
                                                Cancel
                                            </Button>
                                            <ButtonPrimary onClick={() => endTrialEarly()}>End Trial</ButtonPrimary>
                                        </ActionList>
                                    </ModalActions>
                                </Modal>
                            </>
                        )}
                        <AppSection className="field-group">
                            <div className="field-group-content">
                                <div>
                                    <CompanyDetailsSettings formikProps={formikProps} />
                                </div>
                                {formikProps.values.is_universal ? (
                                    <UniversalTccEmbedPanel />
                                ) : (
                                    <div>
                                        <div style={{ marginBottom: 10 }}>
                                            <InputSwitch
                                                labelOn="Gray Background"
                                                labelOff="White Background"
                                                labelPlacement="start"
                                                name="logo_background_color"
                                                checked={formikProps.values.logo_background_color === 'gray'}
                                                onChange={() =>
                                                    formikProps.setFieldValue(
                                                        'logo_background_color',
                                                        formikProps.values.logo_background_color === 'gray'
                                                            ? 'white'
                                                            : 'gray'
                                                    )
                                                }
                                            />
                                        </div>
                                        <S3FileUploader
                                            name="logo_file"
                                            application="company"
                                            formikProps={formikProps}
                                            fileUploaderProps={{
                                                accept: ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'],
                                                previewBackground: formikProps.values.logo_background_color,
                                                onDrop: () => dispatch({ name: 'setFileUploading', payload: true }),
                                                onClearAll: () => {
                                                    formikProps.setFieldValue('logo', '');
                                                    dispatch({ name: 'setFileUploading', payload: false });
                                                },
                                                onRemoveFile: () => {
                                                    formikProps.setFieldValue('logo', '');
                                                    dispatch({ name: 'setFileUploading', payload: false });
                                                }
                                            }}
                                            imageCropperProps={{
                                                onSave: (fileUrl: string) => {
                                                    const fileName = fileUrl.split('/').pop();
                                                    formikProps.setFieldValue('logo', fileName);
                                                    dispatch({ name: 'setFileUploading', payload: false });
                                                },
                                                onCancel: () => dispatch({ name: 'setFileUploading', payload: false })
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </AppSection>
                        {!formikProps.values.is_universal && (
                            <AppSection className="field-group">
                                <div className="field-group-content">
                                    <div>
                                        <CompanyDetailsNotificationSettings formikProps={formikProps} />
                                    </div>
                                    <div>
                                        <CompanyDetailsSMSNumber
                                            isCreateCompanyForm={isCreateCompanyForm}
                                            state={state}
                                            twilioNumbers={twilioNumbers}
                                            masterId={activeCompany?.master_id}
                                            cliqDashboardUrl={cliqDashboardUrl}
                                            smsConfigurations={smsConfigurations}
                                            fetchSmsConfigurations={fetchSmsConfigurations}
                                            syncSmsConfigurations={syncSmsConfigurations}
                                        />

                                        {hasProvisionedSmsNumber && (
                                            <>
                                                <div className="field-group-content" style={{ marginTop: 20 }}>
                                                    <AppSectionHeader title="Message Forwarding" />
                                                    <InputSwitch
                                                        labelOn="Enabled"
                                                        labelOff="Disabled"
                                                        labelPlacement="start"
                                                        disabled={hasTelnyxSmsNumber}
                                                        formikProps={formikProps}
                                                        name={'is_forward_sms_enable'}
                                                        checked={formikProps.values.is_forward_sms_enable}
                                                        onChange={() => {
                                                            if (hasTelnyxSmsNumber) {
                                                                return;
                                                            }

                                                            const newValue = !formikProps.values.is_forward_sms_enable;
                                                            formikProps.setFieldValue('is_forward_sms_enable', newValue);
                                                            if (!newValue) {
                                                                formikProps.setFieldValue('sms_forward_phone_number', null);
                                                                formikProps.setFieldValue(
                                                                    'sms_forward_notification_start_time',
                                                                    null
                                                                );
                                                                formikProps.setFieldValue(
                                                                    'sms_forward_notification_end_time',
                                                                    null
                                                                );
                                                            }
                                                        }}
                                                    />
                                                    {hasTelnyxSmsNumber && (
                                                        <p className="text">
                                                            Message forwarding is not yet available for this number.
                                                            Please check with your admin for future availability of this
                                                            feature.
                                                        </p>
                                                    )}
                                                </div>
                                                {!hasTelnyxSmsNumber && formikProps.values.is_forward_sms_enable && (
                                                    <>
                                                        <InputPhone
                                                            name="sms_forward_phone_number"
                                                            label="Forward Phone Number"
                                                            required={formikProps.values.is_forward_sms_enable}
                                                            formikProps={formikProps}
                                                        />

                                                        <div className="field-group-content">
                                                            <InputSelect
                                                                label="Start Time"
                                                                name="sms_forward_notification_start_time"
                                                                optionLabelKey="time"
                                                                optionValueKey="time"
                                                                required={formikProps.values.is_forward_sms_enable}
                                                                options={TimeRangeList}
                                                                formikProps={formikProps}
                                                            />

                                                            <InputSelect
                                                                label="End Time"
                                                                name="sms_forward_notification_end_time"
                                                                optionLabelKey="time"
                                                                optionValueKey="time"
                                                                required={formikProps.values.is_forward_sms_enable}
                                                                options={TimeRangeList}
                                                                formikProps={formikProps}
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </AppSection>
                        )}

                        {!formikProps.values.is_universal && !isCreateCompanyForm && (
                            <AppSection className="field-group">
                                <SpamManagementSection
                                    companyId={activeCompany.id}
                                    formIsDisabled={formikProps.isSubmitting || isCompanyLightRole}
                                />
                            </AppSection>
                        )}

                        {(isAdminRole || isAgencyRole) && <CompanyDetailsAdminSettings formikProps={formikProps} />}

                        <CompanyDetailsMembers
                            state={state}
                            dispatch={dispatch}
                            formikProps={formikProps}
                            isCreateCompanyForm={isCreateCompanyForm}
                        />

                        <AppFooter sticky={true}>
                            <ActionList position="end">
                                {formikProps.dirty && (
                                    <Button
                                        onClick={() => {
                                            formikProps.resetForm();
                                            dispatch({
                                                name: 'resetCompanyMembers'
                                            });
                                        }}
                                        disabled={state.fileUploading || formikProps.isSubmitting}
                                    >
                                        Cancel
                                    </Button>
                                )}
                                <ButtonPrimary
                                    type="submit"
                                    disabled={state.fileUploading || !formikProps.dirty || formikProps.isSubmitting}
                                >
                                    {isCreateCompanyForm
                                        ? formikProps.isSubmitting
                                            ? 'Adding...'
                                            : 'Add Company'
                                        : formikProps.isSubmitting
                                            ? 'Saving...'
                                            : 'Save'}
                                </ButtonPrimary>
                            </ActionList>
                        </AppFooter>
                    </>
                )}
            </Form>
        </div>
    );
});
